/**
 * @fileoverview Report Constants for PSP/CRE Tool v2.0 - Tab 5 Reports
 * 
 * PURPOSE:
 * Centralized constants for NATO-compliant medical reporting including:
 * - Report templates and metadata
 * - Status thresholds (GREEN/AMBER/RED)
 * - Report frequency and timing
 * - NATO STANAG references
 * - Export format configurations
 * 
 * EDUCATIONAL NOTE:
 * NATO medical reporting follows standardized formats (STANAGs) to ensure
 * interoperability between multinational forces. This file defines those
 * standards for 12 different report types used in military medical planning.
 * 
 * ARCHITECTURE:
 * - Single source of truth for all report configurations
 * - If Romanian Army changes status thresholds, modify only this file
 * - All report generators reference these constants
 * 
 * @version 2.1
 *
 * CHANGELOG:
 * v2.1 (March 14, 2026) - BUG-112: prohibited phrase removed from @author;
 *   non-ASCII >= <= in STATUS_THRESHOLDS replaced with ASCII equivalents.
 * @author Dr. Tatar Radu MD
 */

// ============================================================================
// REPORT METADATA AND CLASSIFICATIONS
// ============================================================================

/**
 * Report Categories
 * Groups reports by their primary purpose in military medical operations
 */
export const REPORT_CATEGORIES = {
  SITUATIONAL: 'Situational Awareness',
  EVACUATION: 'Evacuation Coordination',
  SURVEILLANCE: 'Medical Surveillance',
  PLANNING: 'Operational Planning',
  INCIDENT: 'Incident Management'
};

/**
 * Report Frequency Standards
 * NATO doctrine specifies reporting intervals for different report types
 */
export const REPORT_FREQUENCIES = {
  REAL_TIME: 'Real-time (as events occur)',
  DAILY: 'Daily',
  WEEKLY: 'Weekly', 
  ON_DEMAND: 'On demand',
  INCIDENT: 'Incident-triggered'
};

/**
 * Export Format Configurations
 * Supported output formats for each report type
 */
export const EXPORT_FORMATS = {
  DOCX: { id: 'docx', label: 'Word Document', extension: '.docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  PDF: { id: 'pdf', label: 'PDF Document', extension: '.pdf', mime: 'application/pdf' },
  XLSX: { id: 'xlsx', label: 'Excel Spreadsheet', extension: '.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  HTML: { id: 'html', label: 'HTML Document', extension: '.html', mime: 'text/html' },
  MARKDOWN: { id: 'markdown', label: 'Markdown', extension: '.md', mime: 'text/markdown' },
  CSV: { id: 'csv', label: 'CSV Data', extension: '.csv', mime: 'text/csv' },
  JSON: { id: 'json', label: 'JSON Data', extension: '.json', mime: 'application/json' }
};

// ============================================================================
// REPORT TEMPLATES AND CONFIGURATIONS
// ============================================================================

/**
 * Master Report Registry
 * Defines all 12 NATO-compliant reports with metadata, sections, and configurations
 * 
 * EDUCATIONAL NOTE:
 * Each report serves a specific purpose in the medical command structure:
 * - MEDSITREP: Daily status for commanders (like a hospital's daily census)
 * - 9-LINE: Emergency evacuation request (like calling 911)
 * - MASCAL: Mass casualty declaration (like hospital disaster protocol)
 */
export const REPORT_TEMPLATES = {
  
  // --------------------------------------------------------------------------
  // 1. MEDSITREP - Medical Situation Report
  // --------------------------------------------------------------------------
  MEDSITREP: {
    id: 'medsitrep',
    name: 'MEDSITREP',
    full_name: 'Medical Situation Report',
    category: REPORT_CATEGORIES.SITUATIONAL,
    frequency: REPORT_FREQUENCIES.DAILY,
    
    nato_reference: {
      stanag: 'STANAG 2542',
      ajmedp: 'AJMedP-1, Section 0402',
      description: 'NATO standard for daily medical situation reporting (MEDSITREP format defined in AJMedP-1 Section 0402 / STANAG 2542). NOTE: STANAG 2348 = AMedP-8.2 Basic Military Medical Report — different document.'
    },
    
    purpose: 'Provides commanders with daily status of medical capabilities, patient load, and resource availability across all MTFs in the AO',
    
    sections: [
      { id: 'deployed_hospital_care', name: 'Deployed Hospital Care', order: 1 },
      { id: 'medevac_assets', name: 'MEDEVAC Assets', order: 2 },
      { id: 'battle_casualties', name: 'Battle Casualties', order: 3 },
      { id: 'dnbi', name: 'Disease and Non-Battle Injuries', order: 4 },
      { id: 'health_risks', name: 'Endemic Health Risks', order: 5 },
      { id: 'medlog', name: 'Medical Logistics', order: 6 },
      { id: 'hns', name: 'Host Nation Support', order: 7 },
      { id: 'other_matters', name: 'Other Matters', order: 8 }
    ],
    
    export_formats: ['docx', 'pdf', 'html', 'markdown'],
    
    data_sources: ['medical_configuration', 'calculation_results', 'logistics'],
    
    educational_note: 'The MEDSITREP is the primary daily report that gives commanders visibility into medical readiness. Think of it as the medical equivalent of a daily SITREP - it answers "Can my medical system handle today\'s operations?"'
  },

  // --------------------------------------------------------------------------
  // 2. MEDASSESSREP - Medical Assessment Report  
  // --------------------------------------------------------------------------
  MEDASSESSREP: {
    id: 'medassessrep',
    name: 'MEDASSESSREP',
    full_name: 'Medical Assessment Report',
    category: REPORT_CATEGORIES.SITUATIONAL,
    frequency: REPORT_FREQUENCIES.WEEKLY,
    
    nato_reference: {
      stanag: null,
      ajmedp: 'AJMedP-1, Section 0403',
      description: 'Comprehensive weekly medical assessment for higher headquarters'
    },
    
    purpose: 'Weekly comprehensive medical assessment including MEDSITREP data plus specialist capabilities, trends analysis, and Medical Advisor recommendations',
    
    sections: [
      { id: 'executive_summary', name: 'Executive Summary', order: 1 },
      { id: 'medsitrep_sections', name: 'All MEDSITREP Sections', order: 2 },
      { id: 'specialist_capabilities', name: 'Specialist Medical Capabilities', order: 3 },
      { id: 'trends_analysis', name: 'Weekly Trends Analysis', order: 4 },
      { id: 'medad_assessment', name: 'Medical Advisor Assessment', order: 5 },
      { id: 'critical_shortfalls', name: 'Critical Shortfalls', order: 6 },
      { id: 'recommendations', name: 'Recommendations', order: 7 }
    ],
    
    export_formats: ['docx', 'pdf', 'html'],
    
    data_sources: ['medical_configuration', 'calculation_results', 'logistics'],
    
    educational_note: 'MEDASSESSREP is the weekly "big picture" report for senior commanders. It includes everything in MEDSITREP plus trend analysis and strategic recommendations from the Medical Advisor (MEDAD).'
  },

  // --------------------------------------------------------------------------
  // 3. 9-LINE MEDEVAC - Emergency Evacuation Request
  // --------------------------------------------------------------------------
  NINE_LINE: {
    id: 'nine_line',
    name: '9-LINE MEDEVAC',
    full_name: '9-Line Medical Evacuation Request',
    category: REPORT_CATEGORIES.EVACUATION,
    frequency: REPORT_FREQUENCIES.REAL_TIME,
    
    nato_reference: {
      stanag: 'STANAG 2087',  // QM-6I-001: AAMedP-1.5 Forward Aeromedical Evacuation (9-line request format)
      ajmedp: 'AAMedP-1.5 (Forward Aeromedical Evacuation)',
      parent_doctrine: 'STANAG 3204 / AAMedP-1.1 (Aeromedical Evacuation — broad framework)',
      description: 'Standard 9-line format for requesting emergency medical evacuation'
    },
    
    purpose: 'Real-time request for emergency medical evacuation from point of injury to medical treatment facility',
    
    sections: [
      { id: 'line_1', name: 'Line 1: Location of Pickup Site', order: 1 },
      { id: 'line_2', name: 'Line 2: Radio Frequency', order: 2 },
      { id: 'line_3', name: 'Line 3: Number of Patients by Precedence', order: 3 },
      { id: 'line_4', name: 'Line 4: Special Equipment Required', order: 4 },
      { id: 'line_5', name: 'Line 5: Number of Patients by Type', order: 5 },
      { id: 'line_6', name: 'Line 6: Security at Pickup Site', order: 6 },
      { id: 'line_7', name: 'Line 7: Method of Marking Pickup Site', order: 7 },
      { id: 'line_8', name: 'Line 8: Patient Nationality and Status', order: 8 },
      { id: 'line_9', name: 'Line 9: NBC Contamination', order: 9 }
    ],
    
    precedence_categories: {
      URGENT: { code: 'A', timeline: '90 minutes', description: 'Life, limb, or eyesight threatening' },
      PRIORITY: { code: 'B', timeline: '4 hours', description: 'Requires prompt medical treatment' },
      ROUTINE: { code: 'C', timeline: '24 hours', description: 'No urgent medical requirement' }
    },
    
    export_formats: ['docx', 'pdf', 'html', 'markdown'],
    
    data_sources: ['calculation_results'],
    
    educational_note: 'The 9-LINE is like calling 911 in civilian medicine - it\'s the standard NATO format for requesting emergency evacuation. All 9 lines must be filled out correctly for rapid response. Line 3 (precedence) determines how fast the helicopter responds.'
  },

  // --------------------------------------------------------------------------
  // 4. EPINATO2 - Epidemiological Surveillance Report
  // --------------------------------------------------------------------------
  EPINATO2: {
    id: 'epinato2',
    name: 'EPINATO2',
    full_name: 'NATO Epidemiological Surveillance Report',
    category: REPORT_CATEGORIES.SURVEILLANCE,
    frequency: REPORT_FREQUENCIES.WEEKLY,
    
    nato_reference: {
      stanag: 'STANAG 2535',  // QM-6I-001: AMedP-4.1 Deployment Health Surveillance — governs EpiNATO-2 system
      ajmedp: 'AMedP-4.1 (Deployment Health Surveillance)',
      description: 'Weekly disease and non-battle injury surveillance via EpiNATO-2. NOTE: STANAG 2558 = AMedP-8.17 = Medical Oxygen Standards (wrong). STANAG 2481 = AMedP-3.2 = Medical Information Collection (related but not primary). Correct: STANAG 2535 = AMedP-4.1 = the NATO standard mandating the EpiNATO-2 system.'
    },
    
    purpose: 'Weekly surveillance of disease and non-battle injuries (DNBI) to identify outbreaks and protect force health',
    
    sections: [
      { id: 'summary', name: 'Weekly Summary', order: 1 },
      { id: 'dnbi_by_category', name: 'DNBI by ICD-10 Category', order: 2 },
      { id: 'trends', name: 'Trend Analysis', order: 3 },
      { id: 'outbreak_alerts', name: 'Outbreak Alerts', order: 4 },
      { id: 'preventive_measures', name: 'Preventive Measures', order: 5 }
    ],
    
    dnbi_categories: 25, // Defined in dnbiRates.js
    
    export_formats: ['xlsx', 'csv', 'pdf', 'html'],
    
    data_sources: ['calculation_results'],
    
    educational_note: 'EPINATO2 tracks diseases and injuries NOT caused by combat (DNBI). In modern operations, DNBI can account for 35% of all casualties. This report helps prevent disease outbreaks before they impact mission readiness.'
  },

  // --------------------------------------------------------------------------
  // 5. COMPONENTS OF CARE MATRIX - Capability Dashboard
  // --------------------------------------------------------------------------
  COMPONENTS_MATRIX: {
    id: 'components_matrix',
    name: 'Components of Care Matrix',
    full_name: 'Medical Components of Care Status Matrix',
    category: REPORT_CATEGORIES.SITUATIONAL,
    frequency: REPORT_FREQUENCIES.DAILY,
    
    nato_reference: {
      stanag: null,
      ajmedp: 'AJMedP-1, Annex 4A',
      description: 'Traffic light assessment of medical capability components'
    },
    
    purpose: 'At-a-glance traffic light (RED/AMBER/GREEN) assessment of all medical system components',
    
    components: [
      { id: 'personnel', name: 'Medical Personnel', weight: 1.0 },
      { id: 'facilities', name: 'Medical Treatment Facilities', weight: 1.5 },
      { id: 'medevac', name: 'Medical Evacuation', weight: 2.0 },
      { id: 'medlog', name: 'Medical Logistics', weight: 1.0 },
      { id: 'hns', name: 'Host Nation Support', weight: 0.5 },
      { id: 'c2', name: 'Medical Command & Control', weight: 1.0 },
      { id: 'overall', name: 'Overall Medical Posture', weight: 2.0 }
    ],
    
    export_formats: ['docx', 'pdf', 'xlsx', 'html'],
    
    data_sources: ['medical_configuration', 'calculation_results', 'logistics'],
    
    educational_note: 'The Components Matrix is like a car dashboard - it gives instant visual feedback (green/amber/red) on each part of the medical system. Commanders can see at a glance if their medical support is ready for operations.'
  },

  // --------------------------------------------------------------------------
  // 6. METHANE REPORT - Major Incident Initial Assessment
  // --------------------------------------------------------------------------
  METHANE: {
    id: 'methane',
    name: 'METHANE Report',
    full_name: 'Major Incident Initial Assessment Report',
    category: REPORT_CATEGORIES.INCIDENT,
    frequency: REPORT_FREQUENCIES.INCIDENT,
    
    nato_reference: {
      stanag: null,
      ajmedp: 'AMedP-1.10 (Mass Casualty Management)',
      description: 'Standardized initial report format for major incidents'
    },
    
    purpose: 'Rapid initial assessment report for major incidents/mass casualties - transmitted immediately from scene',
    
    sections: [
      { id: 'm', name: 'M: Major Incident Declaration', order: 1 },
      { id: 'e', name: 'E: Exact Location', order: 2 },
      { id: 't', name: 'T: Type of Incident', order: 3 },
      { id: 'h', name: 'H: Hazards Present and Potential', order: 4 },
      { id: 'a', name: 'A: Access and Egress Routes', order: 5 },
      { id: 'n', name: 'N: Number of Casualties', order: 6 },
      { id: 'e2', name: 'E: Emergency Services Required', order: 7 }
    ],
    
    mist_section: {
      name: 'MIST (Casualty Details)',
      fields: ['M: Mechanism', 'I: Injuries', 'S: Signs/Vitals', 'T: Treatment Given']
    },
    
    export_formats: ['docx', 'pdf', 'html', 'markdown'],
    
    data_sources: ['calculation_results'],
    
    educational_note: 'METHANE is the first report sent from a major incident scene - like the first radio call from a disaster. It gives commanders immediate situational awareness using a simple acronym format that\'s easy to remember under stress.'
  },

  // --------------------------------------------------------------------------
  // 7. OOSR - Out of Standards Report
  // --------------------------------------------------------------------------
  OOSR: {
    id: 'oosr',
    name: 'OOSR',
    full_name: 'Out of Standards Report',
    category: REPORT_CATEGORIES.EVACUATION,
    frequency: REPORT_FREQUENCIES.INCIDENT,
    
    nato_reference: {
      stanag: null,
      ajmedp: 'AJMedP-1, Section 0412',
      description: 'Reports medical evacuations that exceeded clinical timelines'
    },
    
    purpose: 'Documents any MEDEVAC mission that exceeded NATO 10-1-2 timelines (90 min standard) with analysis of causes and outcomes',
    
    sections: [
      { id: 'mission_details', name: 'Mission Details', order: 1 },
      { id: 'timeline_breakdown', name: 'Timeline Breakdown', order: 2 },
      { id: 'delay_causes', name: 'Causes of Delay', order: 3 },
      { id: 'patient_outcome', name: 'Patient Outcome Assessment', order: 4 },
      { id: 'lessons_identified', name: 'Lessons Identified', order: 5 },
      { id: 'corrective_actions', name: 'Corrective Actions Required', order: 6 }
    ],
    
    timeline_steps: [
      { code: 'A', name: 'Time of Wounding', marker: 'T=0' },
      { code: 'B', name: '9-LINE Request Received', target: '10-15 min' },
      { code: 'C', name: 'Aircraft Launch', target: '15-20 min' },
      { code: 'D', name: 'Wheels Down at Scene', target: '45-60 min' },
      { code: 'E', name: 'Patient Loaded', target: '50-65 min' },
      { code: 'F', name: 'Wheels Up from Scene', target: '55-70 min' },
      { code: 'G', name: 'Wheels Down at MTF', target: '75-90 min' },
      { code: 'H', name: 'Patient in Operating Room', target: '90-120 min' }
    ],
    
    export_formats: ['docx', 'pdf', 'html'],
    
    data_sources: ['calculation_results'],
    
    educational_note: 'OOSR is a quality control report - any time we fail to get a casualty to surgery within 2 hours, we must document why and what we\'ll do differently next time. This drives continuous improvement in the evacuation system.'
  },

  // --------------------------------------------------------------------------
  // 8. CASUALTY_ESTIMATE - Primary Planning Output
  // --------------------------------------------------------------------------
  CASUALTY_ESTIMATE: {
    id: 'casualty_estimate',
    name: 'Casualty Estimate Report',
    full_name: 'Casualty Rate Estimate and Medical Planning Report',
    category: REPORT_CATEGORIES.PLANNING,
    frequency: REPORT_FREQUENCIES.ON_DEMAND,
    
    nato_reference: {
      stanag: 'STANAG 2542',
      ajmedp: 'AJMedP-1 (Allied Joint Medical Planning Doctrine)',
      description: 'Comprehensive casualty estimation for operational planning per AJMedP-1 medical estimate methodology. NOTE: STANAG 2546 = AJMedP-2 = Medical Evacuation — not casualty estimation. AJMedP-5 = MedCIS — not planning doctrine. Correct source: AJMedP-1 / STANAG 2542.'
    },
    
    purpose: 'Primary planning document providing casualty estimates, medical load projections, MTF capacity analysis, and resource requirements',
    
    sections: [
      { id: 'executive_summary', name: 'Executive Summary', order: 1 },
      { id: 'battle_casualties', name: 'Battle Casualties by Unit', order: 2 },
      { id: 'dnbi', name: 'Disease and Non-Battle Injuries', order: 3 },
      { id: 'total_medical_load', name: 'Total Medical Load', order: 4 },
      { id: 'mtf_capacity', name: 'MTF Capacity Analysis', order: 5 },
      { id: 'evacuation_analysis', name: 'Evacuation Analysis', order: 6 },
      { id: 'blue_light_triggers', name: 'Blue Light Protocol Triggers', order: 7 },
      { id: 'planning_factors', name: 'Planning Factors and Assumptions', order: 8 }
    ],
    
    export_formats: ['docx', 'pdf', 'xlsx', 'html'],
    
    data_sources: ['force', 'operation_parameters', 'medical_configuration', 'calculation_results'],
    
    educational_note: 'The Casualty Estimate is the main output of the PSP/CRE Tool - it answers "How many casualties will we have?" and "Can our medical system handle them?" This report drives all medical planning decisions.'
  },

  // --------------------------------------------------------------------------
  // 9. BLUE_LIGHT - Medical System Overload Protocol
  // --------------------------------------------------------------------------
  BLUE_LIGHT: {
    id: 'blue_light',
    name: 'MASCAL Threshold Planning',
    full_name: 'MASCAL Threshold and Medical Surge Planning',
    category: REPORT_CATEGORIES.INCIDENT,
    frequency: REPORT_FREQUENCIES.INCIDENT,

    nato_reference: {
      stanag: 'STANAG 2879',
      ajmedp: 'AMedP-1.10 (Medical Aspects of Mass Casualty Management)',
      description: 'Medical surge planning triggered when system approaches MASCAL thresholds. NOTE: "Blue Light Protocol" is not a NATO term — NATO uses MASCAL per AMedP-1.10 / STANAG 2879. AJMedP-1 Annex 4D = Medical Information Management, not surge protocols.'
    },

    purpose: 'MASCAL threshold analysis and surge planning — provides immediate actions and commander decision points when medical system approaches capacity limits',

    trigger_conditions: [
      'MTF beds >85% occupied',
      'Surgical capacity >90% utilized',
      'ICU beds >95% occupied',
      'MEDEVAC <30% available',
      'Medical materiel <3 days supply',
      'MASCAL declared'
    ],
    
    sections: [
      { id: 'situation', name: 'Current Situation Assessment', order: 1 },
      { id: 'immediate_actions', name: 'Immediate Actions (0-2 hours)', order: 2 },
      { id: 'short_term', name: 'Short-term Actions (2-24 hours)', order: 3 },
      { id: 'planning_actions', name: 'Planning Actions (24-72 hours)', order: 4 },
      { id: 'commander_decision', name: 'Commander Decision Required', order: 5 },
      { id: 'termination_criteria', name: 'Protocol Termination Criteria', order: 6 }
    ],
    
    export_formats: ['docx', 'pdf', 'html'],
    
    data_sources: ['calculation_results'],
    
    educational_note: 'Blue Light is like a hospital\'s "disaster mode" - it\'s declared when the medical system is about to be overwhelmed. It triggers specific emergency actions and brings the issue to the commander for critical decisions like pausing operations.'
  },

  // --------------------------------------------------------------------------
  // 10. MEDEVAC_SUMMARY - Evacuation Coordination Report
  // --------------------------------------------------------------------------
  MEDEVAC_SUMMARY: {
    id: 'medevac_summary',
    name: 'MEDEVAC Summary',
    full_name: 'Medical Evacuation Summary Report',
    category: REPORT_CATEGORIES.EVACUATION,
    frequency: REPORT_FREQUENCIES.DAILY,
    
    nato_reference: {
      stanag: null,
      ajmedp: 'AAMedP-1.1 (Aeromedical Evacuation)',
      description: 'Daily summary of evacuation operations and asset status'
    },
    
    purpose: 'Daily summary of MEDEVAC operations including missions completed, patients evacuated, asset availability, and timeline compliance',
    
    sections: [
      { id: 'missions_summary', name: 'Missions Summary', order: 1 },
      { id: 'asset_status', name: 'Asset Status and Availability', order: 2 },
      { id: 'timeline_performance', name: 'Timeline Performance', order: 3 },
      { id: 'patient_flow', name: 'Patient Flow by Echelon', order: 4 },
      { id: 'issues_delays', name: 'Issues and Delays', order: 5 }
    ],
    
    export_formats: ['docx', 'pdf', 'xlsx', 'html'],
    
    data_sources: ['medical_configuration', 'calculation_results'],
    
    educational_note: 'MEDEVAC Summary tracks how well the evacuation system is performing - are we getting casualties to surgery on time? This report identifies problems in the evacuation chain before they become critical.'
  },

  // --------------------------------------------------------------------------
  // 11. EXECUTIVE_BRIEF - Commander Quick Summary
  // --------------------------------------------------------------------------
  EXECUTIVE_BRIEF: {
    id: 'executive_brief',
    name: 'Executive Brief',
    full_name: 'Medical Executive Brief for Commanders',
    category: REPORT_CATEGORIES.SITUATIONAL,
    frequency: REPORT_FREQUENCIES.ON_DEMAND,
    
    nato_reference: {
      stanag: 'STANAG 2542',
      ajmedp: 'AJMedP-1 (Medical Estimate process) + AJP-5 / COPD (Medical Briefing format)',
      description: 'Concise medical brief for senior commanders — BLUF format. NOTE: AJMedP-1 Section 0401 = General Responsibilities (not a briefing format). Correct source: AJMedP-1 medical estimate output + COPD medical briefing requirements.'
    },
    
    purpose: 'One-page executive summary of medical posture, key issues, and recommendations for senior commanders',
    
    sections: [
      { id: 'bottom_line_up_front', name: 'Bottom Line Up Front (BLUF)', order: 1 },
      { id: 'medical_readiness', name: 'Medical Readiness Status', order: 2 },
      { id: 'key_metrics', name: 'Key Metrics', order: 3 },
      { id: 'critical_issues', name: 'Critical Issues', order: 4 },
      { id: 'recommendations', name: 'Recommendations', order: 5 }
    ],
    
    export_formats: ['docx', 'pdf', 'html'],
    
    data_sources: ['calculation_results'],
    
    educational_note: 'The Executive Brief is for busy commanders who need the medical situation in 60 seconds. It follows the military "BLUF" format - Bottom Line Up Front - putting the most important info first.'
  },

  // --------------------------------------------------------------------------
  // 12. NATO_PLANNING_FORMAT - Comprehensive Planning Document
  // --------------------------------------------------------------------------
  NATO_PLANNING: {
    id: 'nato_planning',
    name: 'NATO Planning Format',
    full_name: 'NATO Medical Planning Format Document',
    category: REPORT_CATEGORIES.PLANNING,
    frequency: REPORT_FREQUENCIES.ON_DEMAND,
    
    nato_reference: {
      stanag: 'STANAG 2542',
      ajmedp: 'AJMedP-1 (Allied Joint Medical Planning Doctrine) + AJP-5 / COPD (OPORD Annex Q)',
      description: 'Standardized OPORD Annex Q Medical Support Plan format per AJMedP-1 / STANAG 2542 and AJP-5 planning process. NOTE: STANAG 2879 = AMedP-1.10 = MASCAL management — not the planning format STANAG. AJMedP-5 = MedCIS — not planning doctrine.'
    },
    
    purpose: 'Comprehensive medical planning document in NATO standardized format for submission to higher headquarters',
    
    sections: [
      { id: 'situation', name: '1. SITUATION', order: 1 },
      { id: 'mission', name: '2. MISSION', order: 2 },
      { id: 'execution', name: '3. EXECUTION', order: 3 },
      { id: 'service_support', name: '4. SERVICE SUPPORT', order: 4 },
      { id: 'command_signal', name: '5. COMMAND AND SIGNAL', order: 5 },
      { id: 'annexes', name: 'ANNEXES', order: 6 }
    ],
    
    export_formats: ['docx', 'pdf'],
    
    data_sources: ['force', 'operation_parameters', 'medical_configuration', 'calculation_results', 'logistics'],
    
    educational_note: 'The NATO Planning Format follows the standard military 5-paragraph order structure (SMEAC). This format is used for all formal military planning across NATO nations, ensuring everyone speaks the same language.'
  }
};

// ============================================================================
// STATUS THRESHOLDS - TRAFFIC LIGHT SYSTEM
// ============================================================================

/**
 * Components of Care Status Thresholds
 * Defines GREEN/AMBER/RED thresholds for each medical capability component
 * 
 * EDUCATIONAL NOTE:
 * The traffic light system (GREEN/AMBER/RED) is a quick visual way to assess
 * capability status. Think of it like fuel gauge colors:
 * - GREEN: Full tank, ready for anything
 * - AMBER: Getting low, need to watch closely  
 * - RED: Critical, immediate action required
 * 
 * ARCHITECTURE NOTE:
 * If Romanian Army decides Role 3 should be at different threshold due to 
 * operational constraints, modify only this constant - all reports update automatically
 */
export const STATUS_THRESHOLDS = {
  
  // Medical Personnel
  PERSONNEL: {
    GREEN: { min: 90, description: '>=90% personnel available' },
    AMBER: { min: 70, max: 89, description: '70-89% personnel available' },
    RED: { max: 69, description: '<70% personnel available' }
  },
  
  // Medical Treatment Facilities - Bed Occupancy
  MTF_BEDS: {
    GREEN: { max: 70, description: '<=70% beds occupied' },
    AMBER: { min: 71, max: 85, description: '71-85% beds occupied' },
    RED: { min: 86, description: '>85% beds occupied' }
  },
  
  // Surgical Capacity
  SURGICAL: {
    GREEN: { min: 70, description: '>=70% surgical capacity available' },
    AMBER: { min: 40, max: 69, description: '40-69% surgical capacity available' },
    RED: { max: 39, description: '<40% surgical capacity available' }
  },
  
  // Critical Care (ICU) Beds
  ICU: {
    GREEN: { min: 50, description: '>=50% ICU beds available' },
    AMBER: { min: 20, max: 49, description: '20-49% ICU beds available' },
    RED: { max: 19, description: '<20% ICU beds available' }
  },
  
  // Casualty Staging Unit (CSU) Beds
  CSU: {
    GREEN: { min: 20, description: '>=20% CSU beds available' },
    AMBER: { min: 10, max: 19, description: '10-19% CSU beds available' },
    RED: { max: 9, description: '<10% CSU beds available' }
  },
  
  // MEDEVAC Assets - Availability
  MEDEVAC: {
    GREEN: { min: 80, description: '>=80% MEDEVAC assets available' },
    AMBER: { min: 50, max: 79, description: '50-79% MEDEVAC assets available' },
    RED: { max: 49, description: '<50% MEDEVAC assets available' }
  },
  
  // TACEVAC (Tactical Evacuation) Assets
  TACEVAC: {
    GREEN: { min: 80, description: '>=80% TACEVAC assets available' },
    AMBER: { min: 60, max: 79, description: '60-79% TACEVAC assets available' },
    RED: { max: 59, description: '<60% TACEVAC assets available' }
  },
  
  // Medical Logistics - Medical Materiel Days of Supply (NATO: AMedP-1.12 / STANAG 2128)
  // NOTE: 'Class VIII' is a US Army term (ATP 4-02). NATO uses 'Medical Materiel' per AJP-4.10C / AJMedP-1.
  MEDICAL_MATERIEL: {
    GREEN: { min: 7, description: '>=7 days of supply' },
    AMBER: { min: 4, max: 6, description: '4-6 days of supply' },
    RED: { max: 3, description: '<4 days of supply' }
  },
  
  // Blood Products
  BLOOD: {
    GREEN: { min: 80, description: '>=80% blood requirement met' },
    AMBER: { min: 60, max: 79, description: '60-79% blood requirement met' },
    RED: { max: 59, description: '<60% blood requirement met' }
  },
  
  // Golden Hour Achievement Rate
  GOLDEN_HOUR: {
    GREEN: { min: 90, description: '>=90% casualties reach surgery within 2 hours' },
    AMBER: { min: 70, max: 89, description: '70-89% casualties reach surgery within 2 hours' },
    RED: { max: 69, description: '<70% casualties reach surgery within 2 hours' }
  },
  
  // Overall Medical Posture (Composite)
  OVERALL: {
    GREEN: { min: 80, description: 'All critical components GREEN' },
    AMBER: { min: 60, max: 79, description: 'One or more components AMBER' },
    RED: { max: 59, description: 'One or more components RED' }
  }
};

// ============================================================================
// REPORT DATA MAPPINGS
// ============================================================================

/**
 * Data Field Mappings
 * Maps calculation results to report fields for each report type
 * 
 * This allows report generators to automatically pull the right data
 * from casualtyCalculator.js output without hardcoding paths
 */
export const DATA_MAPPINGS = {
  
  MEDSITREP: {
    deployed_hospital_care: [
      'medical_capacity.role2_basic',
      'medical_capacity.role2_enhanced', 
      'medical_capacity.role3'
    ],
    medevac_assets: [
      'evacuation.medevac_requirements',
      'evacuation.effective_availability'
    ],
    battle_casualties: [
      'battle_casualties.total_bc',
      'battle_casualties.kia',
      'battle_casualties.dow',
      'battle_casualties.wia'
    ],
    dnbi: [
      'dnbi.total_dnbi',
      'dnbi.by_category'
    ],
    medlog: [
      'logistics.class_viii_requirements',
      'logistics.theatre_holding_policy'
    ]
  },
  
  CASUALTY_ESTIMATE: {
    all: 'calculation_results' // Uses entire calculation output
  },
  
  COMPONENTS_MATRIX: {
    personnel: 'medical_configuration.personnel',
    facilities: 'medical_capacity',
    medevac: 'evacuation',
    medlog: 'logistics'
  }
  
  // Additional mappings for other reports...
};

// ============================================================================
// EDUCATIONAL RESOURCES
// ============================================================================

/**
 * NATO Doctrine References
 * Links to key NATO medical doctrine documents for student education
 */
export const NATO_DOCTRINE_REFERENCES = {
  AJMedP_1: {
    title: 'Allied Joint Medical Publication 1 - Allied Joint Doctrine for Medical Support',
    edition: 'Edition B, Version 1',
    date: 'May 2015',
    description: 'Primary NATO medical doctrine document',
    key_sections: {
      '0401': 'Medical Reporting',
      '0402': 'MEDSITREP Format',
      '0403': 'MEDASSESSREP Format',
      '0412': 'Out of Standards Reporting'
    }
  },
  
  AAMedP_1_1: {
    title: 'Allied Aeromedical Publication 1.1 - Aeromedical Evacuation',
    edition: 'Edition A, Version 1',
    description: 'NATO aeromedical evacuation doctrine',
    key_sections: {
      'Section 3': '9-LINE MEDEVAC Format',
      'Section 4': 'TACEVAC Procedures',
      'Annex B': 'Medical Timelines (10-1-2 Rule)'
    }
  },
  
  AMedP_3_2: {
    title: 'Allied Medical Publication 3.2 - Medical Information Collection and Reporting',
    description: 'Medical surveillance and epidemiological reporting',
    key_sections: {
      'Chapter 3': 'EPINATO2 Format',
      'Annex A': 'ICD-10 Disease Categories'
    }
  }
};

/**
 * Key Educational Concepts
 * Explanations of important military medical concepts for students
 */
export const EDUCATIONAL_CONCEPTS = {
  GOLDEN_HOUR: {
    term: 'Golden Hour',
    definition: 'The critical first hour after severe injury when prompt medical intervention has the greatest impact on survival',
    nato_standard: '10-1-2 Rule: 10 min bleeding control, 1 hour to advanced care, 2 hours to surgery',
    modern_reality: 'Ukraine 2022-2024 shows only 25% golden hour achievement in contested environments'
  },
  
  MASCAL: {
    term: 'MASCAL (Mass Casualty)',
    definition: 'A situation where the number and severity of casualties exceeds the immediate local medical capacity',
    trigger: 'Declared by commander when demand exceeds capability',
    impact: 'Triage changes from "best care for each patient" to "greatest good for greatest number"'
  },
  
  ROLE_SYSTEM: {
    term: 'NATO Role System',
    definition: 'Standardized levels of medical care across NATO forces',
    roles: {
      'Role 1': 'Battalion level - First aid and stabilization',
      'Role 2 Basic': 'Brigade level - Emergency surgery',
      'Role 2 Enhanced': 'Division level - Damage control surgery + ICU',
      'Role 3': 'Corps level - Definitive care',
      'Role 4': 'Strategic - Home nation hospitals'
    }
  },
  
  MEDEVAC_VS_TACEVAC: {
    term: 'MEDEVAC vs TACEVAC',
    medevac: 'Forward evacuation from point of injury to first MTF (usually by helicopter)',
    tacevac: 'Tactical evacuation between MTFs within theater (helicopter or fixed-wing)',
    stratevac: 'Strategic evacuation out of theater to home nation (fixed-wing)'
  }
};

// ============================================================================
// EXPORT ALL CONSTANTS
// ============================================================================

const reportConstantsModule = {
  REPORT_CATEGORIES,
  REPORT_FREQUENCIES,
  EXPORT_FORMATS,
  REPORT_TEMPLATES,
  STATUS_THRESHOLDS,
  DATA_MAPPINGS,
  NATO_DOCTRINE_REFERENCES,
  EDUCATIONAL_CONCEPTS
};

export default reportConstantsModule;
